import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { Calendar, Clock, ExternalLink, Layers, MapPin, Minimize2, Pencil, Repeat, Tags, User, Users, Video, X as XIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSaveEventTags } from '../../hooks/useCalendar';
import TagInput from '../../components/TagInput';
import { CRM_ONLY_NOTE, eventTagErrorText, TAG_ORG_WIDE_HINT } from '../../lib/tagCopy';
import { resolveDisplayZone } from '../../lib/timezone';
import { eventCapabilities, KIND_LABELS, SOURCE_LABELS } from '../../lib/calendar/eventCapabilities';
import { eventDayKey } from '../../lib/calendar/zonedCalendar';
import { eventTint } from '../../components/calendar/EventTagChip';
import { BookingLinkActions, GuestDeclinedNote, dangerBtn, primaryBtn, providerLinkLabel, secondaryBtn, whenLabel } from '../../components/calendar/EventQuickView';
import { EventTagPill } from '../../components/calendar/EventTagPill';
import { DestructiveEventDialog } from '../../components/calendar/DestructiveEventDialog';
import { Skeleton } from '../../components/ui/skeleton';
import { TrackingPanel } from './TrackingPanel';
import { useCalendarEventLookup } from './useCalendarEventLookup';

const HTML_RE = /<\/?[a-z][\s\S]*>/i;

/**
 * The full event view (#680, prototype `isCalFull`) at
 * /calendar/event/:eventId?day=YYYY-MM-DD — a bounded fullscreen surface.
 *
 * Built only from what the aggregated payload carries. The prototype's Chat,
 * Forward, Reminder, Print, RSVP "Change", "View series", "Add a room",
 * "Scheduler" and Linked records have no backend and are omitted, not shown
 * disabled (#680 D13).
 *
 * Tags (#680) are shown and edited here for EVERY event (`caps.canTag`) —
 * including a booking_link chip or a holiday, which the editor cannot open:
 * a tag is CRM-side (#682) and moves nothing at the provider.
 */
export default function CalendarEventPage() {
    const { eventId } = useParams();
    const { user } = useAuth();
    const displayZone = resolveDisplayZone({ user });
    const navigate = useNavigate();
    // #791 E30: only this page matches a `bl_` id to the provider twin that absorbed the
    // chip, and tolerates the day being one off (the decline bell link). The editor does not.
    const { event, isLoading, notFound, dayKey } = useCalendarEventLookup(eventId, displayZone, { matchBookingTwin: true });
    const [confirming, setConfirming] = useState(false);

    const back = (state) => navigate('/calendar', { state: { anchorDay: dayKey || undefined, ...state } });

    if (isLoading) {
        return <div className="flex flex-col h-full min-h-0 p-6 gap-3" aria-busy="true"><Skeleton className="h-8 w-80" /><Skeleton className="h-40 w-full" /></div>;
    }
    if (notFound) {
        return (
            <div className="flex flex-col h-full min-h-0 items-center justify-center gap-3 p-8 text-center" data-testid="event-not-found">
                <Calendar className="w-8 h-8 text-[#C0BAB4]" />
                <p className="text-[14px] font-semibold text-[#0C0A09]">This event is no longer on your calendar.</p>
                <p className="text-[12.5px] text-[#8A837E]">It may have been deleted, moved, or the link is missing its day.</p>
                <Link to="/calendar" className={secondaryBtn}>Back to calendar</Link>
            </div>
        );
    }

    const tint = eventTint(event);
    const caps = eventCapabilities(event);
    const eventDay = eventDayKey(event, displayZone) || dayKey;
    const description = event.description || '';
    const safeHtml = HTML_RE.test(description) ? DOMPurify.sanitize(description, { USE_PROFILES: { html: true } }) : null;
    const attendees = (event.attendees || []).filter(a => a && (a.email || a.name));

    const rows = [
        { icon: Clock, label: 'When', value: `${whenLabel(event, displayZone)} · ${displayZone}` },
        event.location && { icon: MapPin, label: 'Where', value: event.location },
        attendees.length > 0 && { icon: Users, label: 'Attendees', value: attendees.map(a => a.name || a.email).join('; ') },
        event.is_recurring_instance && { icon: Repeat, label: 'Recurrence', value: caps.note },
        { icon: Layers, label: 'Type', value: `${KIND_LABELS[event.kind] || event.kind} · ${SOURCE_LABELS[event.source] || event.source}` },
        event.contact_id && { icon: User, label: 'Contact', value: 'Linked CRM contact', href: `/contacts/${event.contact_id}`, testId: 'open-in-crm' },
    ].filter(Boolean);

    return (
        <div className="flex flex-col h-full min-h-0 bg-white" data-testid="calendar-event-page">
            <div className="flex flex-none items-center gap-2.5 px-[18px] py-[11px] border-b border-[#EDEBE6]">
                <Calendar className="w-4 h-4 text-[#57504B]" />
                <span className="flex-1 min-w-0 text-[13.5px] text-[#57504B] truncate">
                    {event.title || '(No title)'} · {event.is_recurring_instance ? 'Event instance' : (KIND_LABELS[event.kind] || 'Event')}
                </span>
                <button type="button" onClick={() => back({ openEvent: event })} title="Show as pop-up" aria-label="Show as pop-up" className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[#57504B] hover:bg-[#F4F2EE]" data-testid="event-show-popup">
                    <Minimize2 className="w-[15px] h-[15px]" />
                </button>
                <button type="button" onClick={() => back()} title="Back to calendar" aria-label="Back to calendar" className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[#57504B] hover:bg-[#F4F2EE]" data-testid="event-back">
                    <XIcon className="w-[17px] h-[17px]" />
                </button>
            </div>
            <div className="flex flex-none flex-wrap items-center gap-2 px-[18px] py-2.5 border-b border-[#EDEBE6]">
                {event.conference_link && (
                    <a href={event.conference_link} target="_blank" rel="noopener noreferrer" className={primaryBtn} data-testid="join-meeting-link">
                        <Video className="w-3.5 h-3.5" /> Join
                    </a>
                )}
                {caps.canEdit && (
                    <Link to={`/calendar/event/${encodeURIComponent(event.event_id)}/edit?day=${eventDay}`} state={{ event }} className={secondaryBtn} data-testid="edit-event-btn">
                        <Pencil className="w-3.5 h-3.5 text-[#57504B]" /> Edit
                    </Link>
                )}
                {caps.canCancelBooking && (
                    <button type="button" onClick={() => setConfirming(true)} className={dangerBtn} data-testid="cancel-booking-btn"><XIcon className="w-3.5 h-3.5" /> Cancel booking</button>
                )}
                {caps.canDelete && (
                    <button type="button" onClick={() => setConfirming(true)} className={dangerBtn} data-testid="delete-event-btn"><XIcon className="w-3.5 h-3.5" /> Delete</button>
                )}
                {event.booking_link_slug && <BookingLinkActions slug={event.booking_link_slug} />}
                {event.html_link && (
                    <a href={event.html_link} target="_blank" rel="noopener noreferrer" className={secondaryBtn}>
                        <ExternalLink className="w-3.5 h-3.5" /> Open in {providerLinkLabel(event.html_link)}
                    </a>
                )}
            </div>
            <div className="flex flex-1 min-h-0 flex-col lg:flex-row items-stretch overflow-y-auto lg:overflow-hidden">
                <div className="flex-1 min-w-0 lg:overflow-y-auto px-6 pt-[22px] pb-12">
                    <div className="flex items-center gap-2.5">
                        <span className="flex-none w-1 h-[26px] rounded-sm" style={{ background: tint.ink }} aria-hidden="true" />
                        <h1 className={`text-[22px] font-semibold tracking-[-0.02em] text-[#0C0A09] [text-wrap:pretty] ${event.status === 'cancelled' ? 'line-through' : ''}`}>
                            {event.title || '(No title)'}
                        </h1>
                    </div>
                    <GuestDeclinedNote event={event} className="mt-3 p-2.5 rounded-[9px] text-[12.5px]" />
                    {(caps.readOnlyReason) && (
                        <p className="mt-3 p-2.5 rounded-[9px] bg-[#FBFAF9] border border-[#EDEBE6] text-[12px] text-[#57504B]" data-testid={event.source === 'booking_link' ? 'booking-no-calendar-note' : 'event-capability-note'}>
                            {caps.readOnlyReason}
                        </p>
                    )}
                    <dl className="mt-5 flex flex-col gap-0.5">
                        {rows.map(r => (
                            <div key={r.label} className="flex items-start gap-3 py-[11px] border-b border-[#F2F0EC]">
                                <r.icon className="w-4 h-4 flex-none text-[#8A837E] mt-px" aria-hidden="true" />
                                <div className="flex-1 min-w-0">
                                    <dt className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95]">{r.label}</dt>
                                    <dd className="text-[13.5px] text-[#0C0A09] mt-1 leading-[1.55] break-words [text-wrap:pretty]">{r.value}</dd>
                                </div>
                                {r.href && <Link to={r.href} className="flex-none text-[12.5px] font-medium text-[#4D11F6] hover:underline" data-testid={r.testId}>Open contact</Link>}
                            </div>
                        ))}
                        {/* Keyed by event: an open draft never carries over to another event. */}
                        {caps.canTag && <EventTagsRow key={event.event_id} event={event} tagNote={caps.tagNote} />}
                    </dl>
                    <section className="mt-[26px]">
                        <h2 className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95]">Agenda</h2>
                        {!description && <p className="mt-2 text-[13.5px] text-[#A29B95]">No agenda or notes.</p>}
                        {safeHtml && (
                            <div
                                className="mt-2 text-[13.5px] text-[#3A3532] leading-[1.7] [overflow-wrap:anywhere] [&_a]:text-[#4D11F6] [&_a]:underline"
                                // Sanitised with DOMPurify's HTML profile — provider descriptions carry markup.
                                dangerouslySetInnerHTML={{ __html: safeHtml }}
                                data-testid="event-agenda"
                            />
                        )}
                        {description && !safeHtml && (
                            <p className="mt-2 text-[13.5px] text-[#3A3532] leading-[1.7] whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid="event-agenda">{description}</p>
                        )}
                    </section>
                </div>
                <TrackingPanel event={event} />
            </div>
            <DestructiveEventDialog
                event={confirming ? event : null}
                onCancel={() => setConfirming(false)}
                onDone={() => { setConfirming(false); back(); }}
            />
        </div>
    );
}

/**
 * The event's tags, and "Edit tags" in place (#680). Saving is one
 * useSaveEventTags call; the page's event comes from the aggregated query that
 * hook invalidates, so the pills refresh without a page-level invalidation.
 */
function EventTagsRow({ event, tagNote }) {
    const saveTags = useSaveEventTags();
    const [draft, setDraft] = useState(null); // null = not editing; else tag names
    const tags = (event.tags || []).filter(t => t && t.name);

    const save = async () => {
        try {
            const result = await saveTags.mutateAsync({ eventId: event.event_id, before: event.tags || [], afterNames: draft });
            if (result.failed.length) {
                toast.error(`Tags weren't saved: ${eventTagErrorText(result.failed[0].reason)}`);
                return;
            }
            if (result.changed) toast.success('Tags updated');
            setDraft(null);
        } catch (err) {
            toast.error(`Tags weren't saved: ${eventTagErrorText(err)}`);
        }
    };

    // Edit tags / Save / Cancel live in the row's HEADER, above the input:
    // TagInput's suggestion list opens downward and stays open after each pick,
    // so a button under (or wrapped under) the input could be covered at any width.
    return (
        <div className="flex items-start gap-3 py-[11px] border-b border-[#F2F0EC]" data-testid="event-tags">
            <Tags className="w-4 h-4 flex-none text-[#8A837E] mt-px" aria-hidden="true" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-h-[18px]">
                    <dt className="flex-1 text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95]">Tags</dt>
                    {draft === null ? (
                        <button
                            type="button"
                            onClick={() => setDraft(tags.map(t => t.name))}
                            className="flex-none text-[12.5px] font-medium text-[#4D11F6] hover:underline"
                            data-testid="event-edit-tags"
                        >
                            Edit tags
                        </button>
                    ) : (
                        <span className="flex flex-none items-center gap-2">
                            <button type="button" onClick={() => setDraft(null)} disabled={saveTags.isPending} className={secondaryBtn} data-testid="event-tags-cancel">Cancel</button>
                            <button type="button" onClick={save} disabled={saveTags.isPending} className={primaryBtn} data-testid="event-tags-save">Save tags</button>
                        </span>
                    )}
                </div>
                <dd className="mt-1.5">
                    {draft === null ? (
                        tags.length ? (
                            <span className="flex flex-wrap gap-1.5">
                                {tags.map(t => <EventTagPill key={t.tag_id} tag={t} />)}
                            </span>
                        ) : (
                            <span className="text-[13.5px] text-[#A29B95]">No tags</span>
                        )
                    ) : (
                        <TagInput value={draft} onChange={setDraft} placeholder="Add tags" ariaLabel="Tags" createHint={TAG_ORG_WIDE_HINT} />
                    )}
                    <span className="block mt-1.5 text-[11.5px] text-[#A29B95]" data-testid="event-tags-note">
                        {CRM_ONLY_NOTE}{tagNote ? ` ${tagNote}` : ''}
                    </span>
                </dd>
            </div>
        </div>
    );
}
